import React, { useMemo } from 'react';
import propTypes from 'prop-types';
import { NavLink, Link } from 'react-router-dom';
import { UiTable } from 'src/ui';
import { Button, MuiDropdown, MuiDropdownItem } from 'src/ui/mui-ui-kit';;
;
import dayjs from 'dayjs-ext';
import {
  DATE_DAY_SMONTH_YEAR,
  PASSED_BACKGROUND_VALIDATION_STATUSES,
  NEEDSREVIEW_BACKGROUND_VALIDATION_STATUSES,
} from 'src/ui/const';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { includesCaseInsensitive } from '../../ui';
import {
  hasAcknowledgePermissions,
  hasResolvePermissions,
  processTableDataForExport,
} from '../utils';
import { ComplianceActions } from './ComplianceActions';

const dropdownIcon = (
  <Button isColor="white">
    <span className="icon">
      <FontAwesomeIcon
        icon={['fal', 'ellipsis-v']}
        className="has-text-grey"
        size="lg"
      />
    </span>
  </Button>
);

const DropdownCell = ({
  row,
  user,
  setResolving,
  setAcknowledgingId,
  setAddingUserComment,
}) => {
  const canResolve = React.useMemo(() => hasResolvePermissions(user), [user]);
  const canAcknowledge = React.useMemo(() => hasAcknowledgePermissions(user), [
    user,
  ]);
  return (
    <MuiDropdown isAlign="right" renderTrigger={dropdownIcon}>
        <MuiDropdownItem
          tag={NavLink}
          to={`/compliance/background_assessments/edit/${row.value}`}
        >
          Edit
        </MuiDropdownItem>
        {canAcknowledge &&
          row.original.acknowledged === false &&
          !PASSED_BACKGROUND_VALIDATION_STATUSES.includes(
            row.original.backgroundAssessmentTypeResult.result
          ) && (
            <MuiDropdownItem
              tag="a"
              onClick={() => setAcknowledgingId(row.original.id)}
            >
              Acknowledge
            </MuiDropdownItem>
          )}
        {canResolve &&
          row.original.acknowledged === true &&
          row.original.resolved === false &&
          !PASSED_BACKGROUND_VALIDATION_STATUSES.includes(
            row.original.backgroundAssessmentTypeResult.result
          ) && (
            <MuiDropdownItem tag="a" onClick={() => setResolving(row.original.id)}>
              Resolve
            </MuiDropdownItem>
          )}
        <MuiDropdownItem
          tag="a"
          onClick={() => setAddingUserComment(row.original.id)}
        >
          Add Log
        </MuiDropdownItem>
    </MuiDropdown>
  );
};

DropdownCell.propTypes = {
  row: propTypes.object.isRequired,
  user: propTypes.object.isRequired,
  setResolving: propTypes.func.isRequired,
  setAcknowledgingId: propTypes.func.isRequired,
  setAddingUserComment: propTypes.func.isRequired,
};

function resolveData(data) {
  return data.map(fiBackgroundAssessment => {
    const newFiBackgroundAssessment = {
      ...fiBackgroundAssessment,
    };

    let flag = '-';
    let flagClassName = 'is-grey';
    let statusClassName = 'background-is-grey';
    if (
      PASSED_BACKGROUND_VALIDATION_STATUSES.includes(
        fiBackgroundAssessment.backgroundAssessmentTypeResult.result
      )
    ) {
      flag = '-';
      flagClassName = 'is-passed';
      statusClassName = 'background-is-passed';
    } else if (
      NEEDSREVIEW_BACKGROUND_VALIDATION_STATUSES.includes(
        fiBackgroundAssessment.backgroundAssessmentTypeResult.result
      )
    ) {
      flag = '-';
      flagClassName = 'is-acknowledged';
      statusClassName = 'background-is-acknowledged';
    } else if (fiBackgroundAssessment.resolved) {
      flag = '-';
      flagClassName = 'is-resolved';
      statusClassName = 'background-is-resolved';
    } else if (fiBackgroundAssessment.acknowledged) {
      flag = '!';
      flagClassName = 'is-acknowledged';
      statusClassName = 'background-is-acknowledged';
    } else {
      flagClassName = 'is-danger';
      flag = '!';
      statusClassName = 'background-is-danger';
    }
    newFiBackgroundAssessment.flag = flag;
    newFiBackgroundAssessment.flagClassName = flagClassName;
    newFiBackgroundAssessment.statusClassName = statusClassName;

    return newFiBackgroundAssessment;
  });
}

export function ComplianceTable({
  loading,
  data,
  user,
  onUpdate,
  dataCallback,
}) {
  const [acknowledgingId, setAcknowledgingId] = React.useState(null);
  const [resolving, setResolving] = React.useState(null);
  const [addingUserComment, setAddingUserComment] = React.useState(null);

  const onAcknowledge = React.useCallback(() => {
    setAcknowledgingId(null);
    onUpdate();
  }, []);

  const onResolve = React.useCallback(() => {
    setResolving(null);
    onUpdate();
  }, []);

  const onAddCommentLog = React.useCallback(() => {
    setAddingUserComment(null);
    onUpdate();
  }, []);

  const dataCallbackHandler = React.useCallback(
    filteredData => {
      if (filteredData.length) {
        dataCallback(processTableDataForExport(filteredData));
      }
    },
    [dataCallback]
  );

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Account',
        accessor: 'account.businessName',
        filterMethod: includesCaseInsensitive,
        Cell: row => (
          <div className="account-name">
            <Link
              className="edit-link"
              to={{
                pathname: `/compliance/background_assessments/edit/${row.original.id}`,
                state: {
                  from: 'dashboard',
                },
              }}
            >
              {row.value}
            </Link>
          </div>
        ),
      },
      {
        Header: 'Date',
        accessor: 'date',
        filterable: false,
        width: 140,
        Cell: row => (
          <>
            {dayjs(row.value)
              .utc()
              .format(DATE_DAY_SMONTH_YEAR)}
          </>
        ),
      },
      {
        Header: 'Type',
        accessor: 'backgroundAssessmentType.type',
        filterMethod: includesCaseInsensitive,
        width: 200,
        Cell: row => <div>{row.value}</div>,
      },
      {
        Header: 'Result',
        accessor: 'backgroundAssessmentTypeResult.result',
        filterMethod: includesCaseInsensitive,
        width: 150,
        Cell: row => <div>{row.value}</div>,
      },
      {
        Header: () => (
          <span className="icon is-small has-text-grey">
            <FontAwesomeIcon icon={['fas', 'pennant']} />
          </span>
        ),
        accessor: 'flag',
        filterable: false,
        Cell: row => (
          <span
            className={`
              }flag-badge ${row.original.flagClassName}`}
          >
            {row.value}
          </span>
        ),
        resizable: false,
        width: 45,
      },
      {
        Header: 'Resolved',
        filterable: false,
        accessor: '',
        width: 120,
        Cell: row => {
          let rowCell;

          if (row.value.resolved === true) {
            rowCell = <div className="resolved">Yes</div>;
          } else if (
            PASSED_BACKGROUND_VALIDATION_STATUSES.includes(
              row.value.backgroundAssessmentTypeResult.result
            )
          ) {
            rowCell = <div className="resolved">-</div>;
          } else {
            rowCell = (
              <div className={`resolved ${row.original.flagClassName}`}>No</div>
            );
          }

          return rowCell;
        },
      },
      {
        Header: '',
        accessor: 'id',
        sortable: false,
        width: 62,
        resizable: false,
        filterable: false,
        Cell: row => (
          <DropdownCell
            setAcknowledgingId={setAcknowledgingId}
            setResolving={setResolving}
            setAddingUserComment={setAddingUserComment}
            row={row}
            user={user}
          />
        ),
      },
    ],
    [user]
  );

  return (
    <>
      <UiTable
        useTablePagination
        className="compliance-table"
        columns={tableColumns}
        loading={loading}
        data={data}
        dataCallback={dataCallbackHandler}
        resolveData={resolveData}
        getTheadThProps={() => ({
          className: 'has-text-left',
        })}
        title="Background Assessment"
        filterable
        hideNext
        hidePrev
        hideNoDataComponent
      />
      <ComplianceActions
        acknowledging={acknowledgingId}
        resolving={resolving}
        addingUserComment={addingUserComment}
        onAcknowledge={onAcknowledge}
        onResolve={onResolve}
        onAddUserComment={onAddCommentLog}
      />
    </>
  );
}

ComplianceTable.propTypes = {
  loading: propTypes.bool.isRequired,
  data: propTypes.arrayOf(propTypes.object).isRequired,
  user: propTypes.object.isRequired,
  dataCallback: propTypes.func,
  onUpdate: propTypes.func,
};

ComplianceTable.defaultProps = {
  onUpdate: () => {},
  dataCallback: () => {},
};
